import { useState, useMemo, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import Pagination from '@/components/feature/Pagination';
import SEOHead from '@/components/feature/SEOHead';
import { siteConfig, seoMeta } from '@/mocks/siteConfig';
import { categories } from '@/mocks/categories';
import { comics, getComicsByCategory } from '@/mocks/comics';

const ITEMS_PER_PAGE = 12;

export default function Category() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeSlug = searchParams.get('cat') || '';
  const activeCategory = categories.find((c) => c.slug === activeSlug);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<'popular' | 'latest'>('popular');
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const filteredComics = useMemo(() => {
    let result = activeCategory ? getComicsByCategory(activeCategory.id) : [...comics];
    if (sortBy === 'popular') {
      result.sort((a, b) => b.views - a.views);
    } else {
      result.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    }
    return result;
  }, [activeCategory, sortBy]);

  const totalPages = Math.ceil(filteredComics.length / ITEMS_PER_PAGE);
  const pagedComics = filteredComics.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [activeSlug, sortBy]);

  const handleCategoryChange = (slug: string) => {
    if (slug) {
      setSearchParams({ cat: slug });
    } else {
      setSearchParams({});
    }
    setMobileFilterOpen(false);
  };

  const pageTitle = activeCategory
    ? `${activeCategory.name} - ${seoMeta.category.title}`
    : seoMeta.category.title;
  const pageDescription = activeCategory
    ? `韩漫阁${activeCategory.name}专区，收录${activeCategory.count}+部优质韩国漫画。${activeCategory.description}。免费无遮下拉式阅读，每日更新。`
    : seoMeta.category.description;
  const pageKeywords = activeCategory
    ? `${activeCategory.name},韩国漫画,免费韩漫,无遮漫画,下拉式漫画`
    : seoMeta.category.keywords;
  const canonicalPath = activeCategory ? `/category?cat=${activeCategory.slug}` : '/category';

  const collectionSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: pageTitle,
    description: pageDescription,
    url: `${siteConfig.url}${canonicalPath}`,
    isPartOf: { '@type': 'WebSite', name: siteConfig.name, url: siteConfig.url },
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteConfig.name,
    url: siteConfig.url,
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        keywords={pageKeywords}
        canonicalPath={canonicalPath}
        schemaJson={{ ...organizationSchema, ...collectionSchema }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Category Header */}
        <header className="relative bg-foreground-950 pt-20 pb-16 md:pt-24 md:pb-20">
          <div className="absolute inset-0 overflow-hidden opacity-20">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary-500/30 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-accent-500/20 rounded-full blur-3xl"></div>
          </div>
          <div className="relative z-10 w-full max-w-6xl mx-auto px-4 md:px-6 text-center">
            <p className="text-primary-400 text-xs md:text-sm font-medium tracking-wider mb-3">
              {activeCategory ? activeCategory.name : '全部分类'}
            </p>
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-background-50 mb-4">
              {activeCategory ? activeCategory.name : '漫画分类浏览'}
            </h1>
            <p className="text-foreground-400 text-sm md:text-base max-w-lg mx-auto">
              {activeCategory ? activeCategory.description : '探索海量韩国漫画，发现你的阅读新世界'}
            </p>

            {/* category count cards */}
            <div className="flex flex-wrap items-center justify-center gap-3 mt-10">
              {categories.slice(0, 5).map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.slug === activeSlug ? '' : cat.slug)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
                    cat.slug === activeSlug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-foreground-800 text-foreground-300 hover:bg-foreground-700'
                  }`}
                >
                  <i className={`${cat.icon} text-sm`}></i>
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </header>

        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: activeCategory ? activeCategory.name : '全部分类' },
          ]}
        />

        {/* Content */}
        <main className="w-full max-w-6xl mx-auto px-4 md:px-6 py-10">
          {/* mobile filter */}
          <div className="md:hidden mb-6">
            <button
              onClick={() => setMobileFilterOpen(!mobileFilterOpen)}
              className="flex items-center gap-2 bg-background-100 px-4 py-2 rounded-lg text-sm text-foreground-700 cursor-pointer w-full justify-between"
            >
              <span>{activeCategory ? activeCategory.name : '全部分类'}</span>
              <i className={mobileFilterOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}></i>
            </button>
            {mobileFilterOpen && (
              <div className="grid grid-cols-2 gap-2 mt-3">
                <button
                  onClick={() => handleCategoryChange('')}
                  className={`text-left px-3 py-2 rounded-lg text-xs transition-colors cursor-pointer ${!activeSlug ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200'}`}
                >
                  全部分类
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleCategoryChange(cat.slug)}
                    className={`text-left px-3 py-2 rounded-lg text-xs transition-colors cursor-pointer ${cat.slug === activeSlug ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200'}`}
                  >
                    <i className={`${cat.icon} mr-1`}></i>
                    {cat.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* desktop sort */}
          <div className="flex items-center justify-between mb-8">
            <div className="hidden md:flex items-center gap-2 flex-wrap">
              <button
                onClick={() => handleCategoryChange('')}
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${!activeSlug ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200'}`}
              >
                全部
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryChange(cat.slug)}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${cat.slug === activeSlug ? 'bg-primary-500 text-background-50' : 'bg-background-100 text-foreground-600 hover:bg-background-200'}`}
                >
                  {cat.name}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-3">
              <span className="text-xs text-foreground-500 whitespace-nowrap">共 {filteredComics.length} 部</span>
              <div className="flex bg-background-100 rounded-full p-1">
                <button
                  onClick={() => setSortBy('popular')}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${sortBy === 'popular' ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}
                >
                  最受欢迎
                </button>
                <button
                  onClick={() => setSortBy('latest')}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${sortBy === 'latest' ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}
                >
                  最近更新
                </button>
              </div>
            </div>
          </div>

          {/* grid */}
          {pagedComics.length > 0 ? (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-5">
                {pagedComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} variant="default" />
                ))}
              </div>
              {totalPages > 1 && (
                <div className="mt-12">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 mx-auto mb-4">
                <i className="ri-emotion-sad-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500 text-sm">该分类下暂无漫画</p>
              <button
                onClick={() => handleCategoryChange('')}
                className="mt-4 text-primary-500 text-sm font-medium hover:text-primary-600 transition-colors cursor-pointer"
              >
                查看全部漫画
              </button>
            </div>
          )}
        </main>

        <Footer />
      </div>
    </>
  );
}